import React, { useState, useEffect } from 'react';
import { Badge } from './Badge';

/**
 * GitSyncStatus - Shows code safety and sync status
 * Reliable indicator that code is backed up on GitHub
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api2.amourmobilenails.com/api/v1';

export const GitSyncStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/amn/git-sync-status/`);
        const data = await res.json();
        setStatus(data);
      } catch (err) {
        setStatus({ status: 'error', message: 'Could not fetch sync status' });
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    // Refresh every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <span className="text-xs text-gray-400">...</span>;
  }

  if (!status || status.status === 'unknown') {
    return (
      <Badge variant="neutral" small>
        No sync yet
      </Badge>
    );
  }

  if (status.status === 'error' || status.status === 'failed') {
    return (
      <Badge variant="error" small title={status.error || status.message || 'Sync failed'}>
        Sync failed
      </Badge>
    );
  }

  // Format the time ago
  const formatAge = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  };

  // Determine safety status
  // Priority: 1) dangling commits, 2) code_safe check, 3) head_on_remote check, 4) stale check
  let variant = 'success';
  let label = '✓ Safe';
  let tooltip = `Code backed up on GitHub\nCommit: ${status.short_sha || status.commit_sha?.slice(0, 7)}\nBranch: ${status.branch}`;

  // Critical: dangling commits means potential lost code
  if (status.dangling_commits > 0) {
    variant = 'error';
    label = `⚠ ${status.dangling_commits} lost`;
    tooltip = `${status.dangling_commits} dangling commit(s) detected!\nThis may be lost code - run recovery\nCommit: ${status.short_sha}`;
  } else if (status.code_safe === false) {
    if (status.head_on_remote) {
      // Code is on GitHub but has uncommitted changes
      variant = 'warning';
      label = `${status.uncommitted_files || '?'} uncommitted`;
      tooltip = `${status.safety_message}\nLast commit (${status.short_sha}) IS on GitHub\nCommit before changes to avoid loss`;
    } else {
      // Code is NOT on GitHub - critical warning
      variant = 'error';
      label = '⚠ Not backed up';
      tooltip = `${status.safety_message}\nCode is NOT on GitHub!\nRun sync immediately`;
    }
  } else if (status.is_stale) {
    variant = 'warning';
    label = `Stale ${formatAge(status.age_hours)}`;
    tooltip = `No sync in ${formatAge(status.age_hours)}\nCommit: ${status.short_sha}\nCheck automation`;
  }

  // Add sync time and ahead/behind info to tooltip
  if (status.commits_ahead > 0 || status.commits_behind > 0) {
    tooltip += `\n${status.commits_ahead || 0} ahead, ${status.commits_behind || 0} behind origin`;
  }
  tooltip += `\nSynced: ${formatAge(status.age_hours)} ago`;

  // Add warnings about unmerged branches and dangling commits
  if (status.unmerged_branches > 0) {
    tooltip += `\n⚠ ${status.unmerged_branches} unmerged branch(es)`;
  }
  if (status.dangling_commits > 0) {
    tooltip += `\n⚠ ${status.dangling_commits} dangling commit(s) - potential lost code!`;
  }
  if (status.recovery_branches > 0) {
    tooltip += `\n📦 ${status.recovery_branches} recovery branch(es) preserved`;
  }

  return (
    <a
      href={status.pr_url || `https://github.com/Moreas/amn_back_end_v2/commits/${status.branch}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
      title={tooltip}
    >
      <Badge variant={variant} small>
        {label}
      </Badge>
    </a>
  );
};

export default GitSyncStatus;
